import { useQuery } from "@tanstack/react-query";

import { useCurrentUser } from "@/auth";
import { pb } from "./pb";
import { queryKeys } from "./queryKeys";
import { useRealtimeCollection } from "./realtime";

export type OrgMembership = {
  /** The account is on the org members list: it sees organisation data (Ads Queue). */
  isMember: boolean;
  /** It may also change that data; only its agents do, through MCP. */
  canWrite: boolean;
  /** Not known yet: no answer from the server so far. */
  isPending: boolean;
};

/**
 * Whether the signed-in account belongs to the organisation.
 *
 * Reads the caller's own `org_members` row, the only one its rules let it see.
 * This drives what the app shows; the server refuses non-members regardless.
 * An unanswered query is `isPending`, never "not a member", so a deep link to an
 * organisation page is not redirected away while the answer is on its way.
 */
export function useOrgMembership(): OrgMembership {
  const userId = useCurrentUser()?.id ?? "";
  useRealtimeCollection("org_members", { enabled: Boolean(userId) });
  const query = useQuery({
    queryKey: queryKeys.collection("org_members", userId),
    queryFn: async ({ signal }) => {
      const page = await pb.collection("org_members").getList(1, 1, {
        requestKey: null,
        signal,
        filter: pb.filter("user = {:user}", { user: userId }),
        fields: "id,can_write",
        skipTotal: true,
      });
      return page.items[0] ?? null;
    },
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
  });
  const row = query.data;
  return {
    isMember: Boolean(row),
    canWrite: Boolean(row?.can_write),
    isPending: Boolean(userId) && row === undefined,
  };
}
